/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Card,
    Typography,
    Button,
    IconButton,
    Box,
    InputLabel,
    OutlinedInput,
    Avatar,
    Tooltip,
    LinearProgress,
    Badge
} from '@mui/material';
import { FiX, FiPlus } from 'react-icons/fi';
import { RiUploadCloud2Line } from 'react-icons/ri';
import axios from 'lib/axios';
import PdfIcon from '../../assets/static_images/pdficon.svg';
import Upload from '../../assets/static_images/uploadpolicyactive.svg';
import UserSelectionModal from './UserSelectionModal';
import { useDispatch } from 'react-redux';
import { activeLoader } from 'store/reducers/loaderSlice';
import { toast } from 'sonner';

const CreatePolicy = ({ setOpenModal, policy = null, setPolicies, setOpenOption }) => {
    const pdfInput = useRef(null);
    const dispatch = useDispatch();

    // State management
    const [postPdfs, setPostPdfs] = useState([]);
    const [postPdfNames, setPostPdfNames] = useState([]);
    const [postPdfsData, setPostPdfsData] = useState([]);
    const [eventName, setEventName] = useState(() => {
        return policy != null ? policy.policy_title : '';
    });
    const [text, setText] = useState(() => {
        return policy != null ? policy.plain_text_body : '';
    });

    const [existingpostPdfs, setExistingpostPdfs] = useState(policy && policy.video ? policy.video.split(',') : []);
    const [submitUrl, setSubmitUrl] = useState(() => {
        return policy != null ? '/policies/update' : '/policies';
    });
    const [processing, setProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [validatorMessage, setValidatorMessage] = useState([]);
    const [showViewerModal, setShowViewerModal] = useState(false);

    // Viewer selection states
    const [selectedUsers, setSelectedUsers] = useState(() => {
        return policy
            ? policy.policy_users.map((user) => ({
                  user_id: user.user.user_id,
                  first_name: user.user.first_name,
                  last_name: user.user.last_name,
                  profile_image: user.user.profile_image,
                  job_title: user.user.job_title || []
              }))
            : [];
    });

    const [selectedDepartments, setSelectedDepartments] = useState(() => {
        return policy && policy.policy_departments
            ? policy.policy_departments.map((dept) => ({
                  department_id: dept.department_id,
                  department_name: dept.department_name
              }))
            : [];
    });

    const [selectedDivisions, setSelectedDivisions] = useState(() => {
        return policy && policy.policy_divisions
            ? policy.policy_divisions.map((div) => ({
                  department_division_id: div.department_division_id,
                  division_name: div.division_name
              }))
            : [];
    });

    const removeFile = (index) => {
        if (postPdfs[index]) {
            URL.revokeObjectURL(postPdfs[index]);
        }
        setPostPdfs((prev) => prev.filter((_, i) => i !== index));
        setPostPdfNames((prev) => prev.filter((_, i) => i !== index));
        setPostPdfsData((prev) => prev.filter((_, i) => i !== index));
    };

    const removeExistingFile = (index) => {
        setExistingpostPdfs((prev) => prev.filter((_, i) => i !== index));
    };

    async function convertPdf() {
        const file = pdfInput.current.files[0];
        if (file.size < 52453290) {
            const url = URL.createObjectURL(file);
            setPostPdfsData([...postPdfsData, file]);
            setPostPdfs([...postPdfs, url]);
            setPostPdfNames([...postPdfNames, file.name]);
        } else {
            toast.error('File size must be smaller than 50MB');
        }
    }

    const handleClose = () => {
        postPdfs.forEach((url) => {
            if (url) URL.revokeObjectURL(url);
        });
        setOpenModal(false);
    };

    const handleViewersSelected = (users) => {
        setSelectedUsers(users);
        setShowViewerModal(false);
    };

    function submitEvent() {
        setValidatorMessage([]);
        const validationErrors = [];

        if (eventName === '') {
            validationErrors.push({
                field: 'eventName',
                message: 'Policy name is required'
            });
        }
        if (text === '') {
            validationErrors.push({
                field: 'description',
                message: 'Description is required'
            });
        }

        if (validationErrors.length > 0) {
            setValidatorMessage(validationErrors);
            return;
        }

        const formData = new FormData();
        formData.append('title', eventName);
        formData.append('content', text);

        if (policy != null) {
            formData.append('policy_id', policy.policy_id);
        }

        // Add viewers
        if (selectedUsers.length > 0) {
            formData.append(
                'viewers',
                JSON.stringify(
                    selectedUsers.map((user) => ({
                        id: user.user_id,
                        name: user.first_name
                    }))
                )
            );
        }
        if (selectedDepartments.length > 0) {
            formData.append(
                'viewers_department_ids',
                JSON.stringify(
                    selectedDepartments.map((dept) => ({
                        id: dept.department_id,
                        name: dept.department_name
                    }))
                )
            );
        }
        if (selectedDivisions.length > 0) {
            formData.append(
                'viewers_department_division_ids',
                JSON.stringify(
                    selectedDivisions.map((div) => ({
                        id: div.department_division_id,
                        name: div.division_name
                    }))
                )
            );
        }

        // pdfs
        formData.append('pdf_exist', existingpostPdfs.length > 0 ? true : false);
        formData.append('new_pdf_exist', postPdfsData.length > 0 ? true : false);
        formData.append('old_pdfs', JSON.stringify(existingpostPdfs));
        formData.append('post_pdf_names', JSON.stringify(postPdfNames));
        formData.append('post_pdfs', JSON.stringify(postPdfsData));

        postPdfsData.forEach((pdf, index) => {
            formData.append(`docs-${index}`, pdf);
        });

        dispatch(activeLoader(true));
        setProcessing(true);
        setUploadProgress(0);

        axios
            .post(`${submitUrl}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            })
            .then(({ data }) => {
                if (data.success) {
                    dispatch(activeLoader(false));
                    toast.success(data.message || 'Policy saved successfully');
                    setOpenModal(false);
                    if (setOpenOption) {
                        setOpenOption(false);
                    }
                    console.log('-------------------', data.policy);
                    if (policy) {
                        setPolicies((p) => p.map((policy) => (policy.policy_id === data.policy.policy_id ? data.policy : policy)));
                    } else {
                        setPolicies((policies) => [data.policy, ...policies]);
                    }
                }
            })
            .catch((err) => {
                dispatch(activeLoader(false));
                console.log(err);
                toast.error('Failed to save policy');
            })
            .finally(() => {
                setProcessing(false);
                setUploadProgress(0);
            });
    }

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 1000
                }}
            >
                <Card
                    sx={{
                        width: { xs: '95%', md: '700px' },
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        position: 'relative',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        backgroundColor: 'white',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img src={Upload} alt="edit" />
                                <Typography color="primary" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                    {policy ? 'Edit' : 'Create'} Policy & Procedure
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    color: 'text.secondary',
                                    bgcolor: 'grey.100',
                                    borderRadius: '50%',
                                    '&:hover': { bgcolor: 'grey.300' }
                                }}
                            >
                                <FiX size={17} />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <InputLabel sx={{ color: 'text.primary', fontWeight: 500 }}>Policy Name *</InputLabel>
                                <OutlinedInput
                                    value={eventName}
                                    onChange={(e) => {
                                        setEventName(e.target.value);
                                        setValidatorMessage((prev) => prev.filter((item) => item.field !== 'eventName'));
                                    }}
                                    placeholder="Type policy name here..."
                                    fullWidth
                                    sx={{ borderRadius: 1, fontSize: '1rem', background: '#F8FAFC' }}
                                />
                                {validatorMessage.find((error) => error.field === 'eventName') && (
                                    <Typography color="error" variant="caption">
                                        {validatorMessage.find((error) => error.field === 'eventName').message}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography>Description</Typography>
                                    <Typography color="text.secondary" variant="caption">
                                        (required)
                                    </Typography>
                                </Box>
                                <textarea
                                    value={text}
                                    onChange={(e) => {
                                        setText(e.target.value);
                                        setValidatorMessage((prev) => prev.filter((item) => item.field !== 'description'));
                                    }}
                                    placeholder="Type description here..."
                                    style={{
                                        width: '100%',
                                        minHeight: '120px',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #E2E8F0',
                                        background: '#F8FAFC',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        fontSize: '1rem',
                                        outline: 'none'
                                    }}
                                />
                                {validatorMessage.find((error) => error.field === 'description') && (
                                    <Typography color="error" variant="caption">
                                        {validatorMessage.find((error) => error.field === 'description').message}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Viewers
                                </Typography>
                                <Box
                                    onClick={() => setShowViewerModal(true)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                        backgroundColor: '#F8FAFC',
                                        border: '1px solid #E2E8F0',
                                        '&:hover': {
                                            backgroundColor: '#F1F5F9',
                                            borderColor: '#CBD5E1'
                                        },
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <img src={Upload} alt="Share" />
                                    <Typography ml={1}>
                                        {selectedUsers.length > 0 || selectedDepartments.length > 0 || selectedDivisions.length > 0
                                            ? 'Manage Viewers'
                                            : 'Add Viewers'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedUsers.map((selectedUser) => (
                                    <Box
                                        key={selectedUser.user_id}
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1.5,
                                            pr: 4,
                                            borderRadius: '8px',
                                            backgroundColor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            '&:hover': {
                                                backgroundColor: '#F1F5F9',
                                                borderColor: '#CBD5E1',
                                                '& .delete-button': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)'
                                                }
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <Avatar
                                            src={selectedUser?.profile_image}
                                            alt={selectedUser?.first_name}
                                            sx={{ width: 36, height: 36 }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {selectedUser?.first_name} {selectedUser?.last_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                                {selectedUser?.job_title?.[0]?.title || 'User'}
                                            </Typography>
                                        </Box>
                                        <Tooltip title="Remove viewer" placement="top" arrow enterDelay={200} leaveDelay={200}>
                                            <IconButton
                                                className="delete-button"
                                                onClick={() => {
                                                    setSelectedUsers((prev) =>
                                                        prev.filter((user) => user.user_id !== selectedUser.user_id)
                                                    );
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 1,
                                                    top: 1,
                                                    transform: 'translateY(-50%)',
                                                    opacity: 0,
                                                    width: 24,
                                                    height: 24,
                                                    color: 'text.secondary',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(4px)',
                                                    '&:hover': {
                                                        backgroundColor: 'error.lighter',
                                                        color: 'error.main'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <FiX size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                {selectedDepartments.map((dept) => (
                                    <Box
                                        key={dept.department_id}
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1.5,
                                            pr: 4,
                                            borderRadius: '8px',
                                            backgroundColor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            '&:hover': {
                                                backgroundColor: '#F1F5F9',
                                                borderColor: '#CBD5E1',
                                                '& .delete-button': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)'
                                                }
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>D</Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{dept.department_name}</Typography>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Department</Typography>
                                        </Box>
                                        <Tooltip title="Remove department" placement="top" arrow enterDelay={200} leaveDelay={200}>
                                            <IconButton
                                                className="delete-button"
                                                onClick={() => {
                                                    setSelectedDepartments((prev) =>
                                                        prev.filter((d) => d.department_id !== dept.department_id)
                                                    );
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 1,
                                                    top: 1,
                                                    transform: 'translateY(-50%)',
                                                    opacity: 0,
                                                    width: 24,
                                                    height: 24,
                                                    color: 'text.secondary',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(4px)',
                                                    '&:hover': {
                                                        backgroundColor: 'error.lighter',
                                                        color: 'error.main'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <FiX size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                                {selectedDivisions.map((div) => (
                                    <Box
                                        key={div.department_division_id}
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1.5,
                                            pr: 4,
                                            borderRadius: '8px',
                                            backgroundColor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            '&:hover': {
                                                backgroundColor: '#F1F5F9',
                                                borderColor: '#CBD5E1',
                                                '& .delete-button': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)'
                                                }
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>D</Avatar>
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{div.division_name}</Typography>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Division</Typography>
                                        </Box>
                                        <Tooltip title="Remove division" placement="top" arrow enterDelay={200} leaveDelay={200}>
                                            <IconButton
                                                className="delete-button"
                                                onClick={() => {
                                                    setSelectedDivisions((prev) =>
                                                        prev.filter((d) => d.department_division_id !== div.department_division_id)
                                                    );
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 1,
                                                    top: 1,
                                                    transform: 'translateY(-50%)',
                                                    opacity: 0,
                                                    width: 24,
                                                    height: 24,
                                                    color: 'text.secondary',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(4px)',
                                                    '&:hover': {
                                                        backgroundColor: 'error.lighter',
                                                        color: 'error.main'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <FiX size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>

                            {existingpostPdfs.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Existing Files
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {existingpostPdfs.map((video, index) => {
                                            let source = video.split('/')[1];
                                            return (
                                                <Box
                                                    key={`existing-${index}`}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        backgroundColor: '#F8FAFC',
                                                        border: '1px solid #E2E8F0',
                                                        '&:hover': {
                                                            backgroundColor: '#F1F5F9',
                                                            borderColor: '#CBD5E1'
                                                        },
                                                        transition: 'all 0.2s ease',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    <img src={PdfIcon} width={24} height={24} alt="File" style={{ opacity: 0.8 }} />
                                                    <Typography
                                                        sx={{
                                                            ml: 1,
                                                            flex: 1,
                                                            cursor: 'pointer',
                                                            '&:hover': { color: 'primary.main' }
                                                        }}
                                                        onClick={() => {
                                                            window.open(
                                                                `${process.env.REACT_APP_BACKEND_URL}/Uploads/${
                                                                    policy.user_id
                                                                }/Policies/${policy.policy_id.toString()}/${source}`,
                                                                '_blank',
                                                                'fullscreen=yes'
                                                            );
                                                        }}
                                                    >
                                                        {video.split('/')[0]}
                                                    </Typography>
                                                    <Tooltip title="Remove file" placement="top" arrow enterDelay={200} leaveDelay={200}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeExistingFile(index)}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': { color: 'error.main' }
                                                            }}
                                                        >
                                                            <FiX size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Attach Files
                                </Typography>
                                {postPdfs.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {postPdfs.map((pdf, index) => (
                                            <Box
                                                key={`doc-${index}`}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 1.5,
                                                    borderRadius: '8px',
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    '&:hover': {
                                                        backgroundColor: '#F1F5F9',
                                                        borderColor: '#CBD5E1'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <img src={PdfIcon} width={24} height={24} alt="File" style={{ opacity: 0.8 }} />
                                                <Typography
                                                    sx={{
                                                        ml: 1,
                                                        flex: 1,
                                                        cursor: 'pointer',
                                                        '&:hover': { color: 'primary.main' }
                                                    }}
                                                    onClick={() => window.open(pdf, '_blank')}
                                                >
                                                    {postPdfNames[index]}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeFile(index)}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&:hover': { color: 'error.main' }
                                                    }}
                                                >
                                                    <FiX size={16} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => pdfInput.current.click()}
                                            startIcon={<FiPlus size={18} />}
                                            sx={{ mt: 1 }}
                                        >
                                            Add More Files
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box
                                        onClick={() => pdfInput.current.click()}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 4,
                                            borderRadius: '8px',
                                            backgroundColor: '#F8FAFC',
                                            border: '1px dashed #CBD5E1',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#F1F5F9',
                                                borderColor: '#94A3B8'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <RiUploadCloud2Line size={32} style={{ color: '#1E7C88' }} />
                                        <Typography color="primary" sx={{ mt: 1 }}>
                                            Click to upload files
                                        </Typography>
                                    </Box>
                                )}
                                <input
                                    ref={pdfInput}
                                    type="file"
                                    multiple="true"
                                    accept=".pdf,.doc,.docx"
                                    name="pdfInput"
                                    onChange={convertPdf}
                                    style={{ display: 'none' }}
                                />
                            </Box>

                            {uploadProgress > 0 && (
                                <Box sx={{ width: '100%', mb: 1 }}>
                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                        Uploading... {uploadProgress}%
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={submitEvent}
                                disabled={processing}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}
                            >
                                {processing ? 'Processing...' : policy != null ? 'Update Policy' : 'Create Policy'}
                            </Button>
                        </Box>
                    </Box>
                </Card>
            </Box>

            <UserSelectionModal
                open={showViewerModal}
                setOpen={setShowViewerModal}
                onUsersSelected={handleViewersSelected}
                previouslySelectedUsers={selectedUsers}
                title="Select Policy Viewers"
                buttonText="Add Selected Viewers"
            />
        </>
    );
};

export default CreatePolicy;
